import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import en from '../locales/en.json';
import es from '../locales/es.json';

const LanguageContext = createContext(null);

const translations = { en, es };

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Detect browser language
const getBrowserLanguage = () => {
  const browserLang = navigator.language || navigator.userLanguage;
  // Check if browser language starts with 'es' (Spanish)
  if (browserLang && browserLang.toLowerCase().startsWith('es')) {
    return 'es';
  }
  return 'en'; // Default to English
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    // Check localStorage first, then browser language
    const saved = localStorage.getItem('preferred_language');
    return saved || getBrowserLanguage();
  });
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Get nested translation value by key path (e.g., "nav.home")
  const t = useCallback((key, replacements = {}) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Fallback to English if key not found
        value = translations['en'];
        for (const fallbackKey of keys) {
          if (value && typeof value === 'object' && fallbackKey in value) {
            value = value[fallbackKey];
          } else {
            return key; // Return key if not found
          }
        }
        break;
      }
    }
    
    // Handle replacements like {spots}
    if (typeof value === 'string') {
      Object.keys(replacements).forEach(placeholder => {
        value = value.replace(`{${placeholder}}`, replacements[placeholder]);
      });
    }
    
    return value || key;
  }, [language]);

  // Set language and persist
  const setLanguage = useCallback(async (lang) => {
    setLanguageState(lang);
    localStorage.setItem('preferred_language', lang);
    
    // If logged in, save to user account
    if (isLoggedIn) {
      try {
        await axios.put(`${API_URL}/auth/language`, { language: lang }, { withCredentials: true });
      } catch (error) {
        console.error('Failed to save language preference:', error);
      }
    }
  }, [isLoggedIn]);

  // Load user's preferred language from account
  const loadUserLanguage = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`, { withCredentials: true });
      if (response.data && response.data.preferred_language) {
        setLanguageState(response.data.preferred_language);
        localStorage.setItem('preferred_language', response.data.preferred_language);
      }
      setIsLoggedIn(true);
    } catch (error) {
      setIsLoggedIn(false);
    }
  }, []);

  // Check for logged in user on mount
  useEffect(() => {
    loadUserLanguage();
  }, [loadUserLanguage]);

  // Listen for auth changes
  const updateLoginStatus = useCallback((loggedIn) => {
    setIsLoggedIn(loggedIn);
    if (loggedIn) {
      loadUserLanguage();
    }
  }, [loadUserLanguage]);

  return (
    <LanguageContext.Provider value={{ 
      language, 
      setLanguage, 
      t, 
      updateLoginStatus,
      availableLanguages: [
        { code: 'en', name: 'English', flag: '🇺🇸' },
        { code: 'es', name: 'Español', flag: '🇪🇸' }
      ]
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

export default LanguageContext;
